import {Injectable} from '@angular/core';
import {DataService} from "./data.service";
import {map} from 'rxjs';
import {
  ATTACHMENT_KEYS,
  COMMENT_KEYS,
  FORMAT_THOUSANDS_KEYS, NAME_KEYS,
  PERCENTAGE_KEYS, SENSITIVE_KEYS
} from "../shared/constants/fields.constants";
import {Field} from "../common/interfaces/data.interfaces";

export type FieldSemanticType =
  ('attachmentField' | 'commentArray' | 'attachmentArray' | 'percentage' | 'commentField' | 'stringArray' | 'objectArray');

@Injectable({
  providedIn: 'root',
})
export class FieldsService {
  constructor(private ds: DataService) {
  }

  isObjectArray = (f: Field) => this.ds.isArray(f.value) && (this.isObject(f.value?.[0]));

  isStringArray = (f: Field) => this.ds.isArray(f.value) && (this.ds.isString(f.value?.[0]));

  getAttachmentField = (o) => this.fieldsArrayFromObj(o).find(f => this.ds.valueIncludes(this.ds.trim(f.key), [this.ds.getFirstPresentKey(o, ATTACHMENT_KEYS)]));

  isAttachmentArray = (f) => this.ds.isArray(f.value) && (this.ds.valueIncludes(f?.key, ATTACHMENT_KEYS) || this.getAttachmentField(f.value?.[0]));
  // also checks values like .pdf in named paths
  isAttachmentField = (f: Field) => f?.value && ((this.ds.valueIncludes(f?.value, ATTACHMENT_KEYS) && f?.key !== 'name') || this.ds.valueIncludes(f?.key, ATTACHMENT_KEYS));


  getCommentField = (o) => this.fieldsArrayFromObj(o).find(f => this.ds.valueIncludes(this.ds.trim(f.key), [this.ds.getFirstPresentKey(o, COMMENT_KEYS)]));

  isCommentArray = (f: Field) => this.ds.isArray(f.value) && (this.isCommentField(f) || !!this.getCommentField(f.value?.[0]));

  isCommentField = (f: Field) => this.ds.valueIncludes(f?.key, COMMENT_KEYS);


  isPercentageField = (f: Field) => this.ds.valueIncludes(f?.key, PERCENTAGE_KEYS) && this.ds.isNumber(f.value) && (+f.value <= 100);

  isSensitiveField = (f: Field) => this.ds.valueIsEither(f?.key, SENSITIVE_KEYS) && (this.ds.isNumber(f?.value) || this.ds.valueIncludes(f?.key, 'uid') || f.key === 'id');

  isADefinedField = (f: Field) => this.ds.isBool(f?.value) || this.ds.isArray(f?.value) ||
    this.ds.isString(f?.value) || this.isObject(f?.value) || this.ds.isNumber(f?.value);

  isObject = (x) => this.ds.isObject_(x) && !this.ds.isString(x) && !this.ds.isNumber(x) &&
    !this.ds.isArray(x) && this.ds.isDefined(x);

  getFieldByKey = (key: string, fields: Field[]) => {
    return fields
      ?.find(f => this.ds.valueIncludes(f.key, key) && !this.ds.valueIncludes(f.key, 'typename'));
  };

  getFirstPresentField = (o, keys_) => {
    return this.getFields(o)
      .find(f => this.ds.valueIncludes(this.ds.trim(f.key), [this.ds.getFirstPresentKey(o, keys_)]));
  };

  formatFields(fields: Field[]) {
    if (!Array.isArray(fields)) return fields;

    return fields?.map(x => ({
      ...x,
      value: this.ds.formatIfDate(x.value),
      name: this.ds.capitalizeFirstLetter(this.ds.camelToSpaced(x.name)).trim()
    }));
  }

  getFirstNameField(object): Field {
    if (typeof object !== 'object') return object;
    const fields = this.getFields(object)?.filter(f => !this.ds.isArray(f?.value));
    let field: Field;

    //priority as in namesInner_;
    NAME_KEYS.forEach(key => !field && (field = this.getFieldByKey(key, fields)));
    if (field && this.isObject(field.value)) field = this.getFirstNameField(field.value);
    return field;
  }

  separateThousands(value, key) {
    if (this.ds.valueIncludes(key, FORMAT_THOUSANDS_KEYS)) value = this.ds.separateThousands(value);
  }


  // for objects
  getFields(object_) {
    if (typeof object_ !== 'object') return object_;
    // add properties for certains fields types// for indicator id, assign before filtering id
    const object = {...object_};
    let fields = this.fieldsArrayFromObj(object);
    const isValue = (f) => !!f?.value || f?.value === false || f?.value === 0;

    // hide ids if not staff fo // hide hidden,stickly for querying fields //hide empty
    fields = fields?.filter(f => isValue(f))?.filter(f => this.isADefinedField(f));
    return this.formatFields(fields);
  }


  getObjectValue(element: any, key: string, interpretValue = false) {
    if (!key.includes('+')) return this.getSingleObjectValue(element, key);
    const keyValueArray = key?.split('+')?.reverse();
    let inclusiveValue = '';

    for (let i = 0; i <= keyValueArray.length; i++) {
      inclusiveValue = `${inclusiveValue}
      ${this.getSingleObjectValue(element, keyValueArray?.pop()?.trim(), interpretValue)}`;
    }

    return inclusiveValue ?? 'N/A';
  }

  getSingleObjectValue(element: any, key: string, interpretV = false) {
    const keyArray = key?.split('.')?.reverse();
    if (!(keyArray?.length > 0)) return element ?? 'N/A';
    let value = element;
    for (let i = 0; i <= keyArray.length; i++) value = value?.[keyArray.pop()?.trim()];
    return interpretV ? this.interpretValue(value) : value;
  }

  interpretValue(value, key?) {
    const len = value?.toString()?.length;
    const getStringArray = (x) => this.ds.isObject([...value]?.pop()) ?
      value.map(v => this.getFirstNameField(v)?.value) : value;

    if (typeof value === 'number') value = this.separateThousands(value, key);
    else if (typeof value === 'boolean') value = value ? 'YES' : 'NO';
    else if (Array.isArray(value)) this.ds.arrayToDisplayString(getStringArray(value)) ?? 'N/A';
    else if (this.isObject(value)) value = this.getFirstNameField(value)?.value;
    else if (this.ds.isValidDate(value)) value = this.ds.getDisplayDate(value);
    else if (typeof value === 'string' && !value?.toString()?.includes('@')) value = this.ds.replaceWords(value, '_', ' ');
    if (!value?.toString()?.trim()) value = 'N/A';

    return value;
  }

  setFieldsWidths(items: any[], width = 5, max = 60, threshold = 5) {
    const sampleRecord = items[0];
    // for each field get the longest string // max is 100 // min is 8
    const allFields = this.formatFields(this.fieldsArrayFromObj(sampleRecord)).map(field => {
      items.forEach(item => {
        const widthBuffer = item[field.key]?.toString()?.length ?? threshold;
        if (widthBuffer < width) return;
        if (widthBuffer > max) width = max;
        else width = widthBuffer;
      });
      return {...field, length: ((field?.key?.length > width) ? field?.key?.length : width) + threshold};
    });

    return allFields;
  }

  mapVisibleItem(item) {
    const allFields = this.formatFields(this.fieldsArrayFromObj(item));
    const itemMod = {};

    allFields.forEach(field => {
      if (this.ds.isObject(field.value)) itemMod[field.key] = this.getFirstNameField(field.value)?.value ?? '';
      else if (this.ds.isArray(field.value)) itemMod[field.key] = this.ds.arrayToString(field?.value?.map(o => this.getFirstNameField(o)?.value ?? '')
    )
      ;
    else
      itemMod[field.key] = field.value ?? '';
    });
    return itemMod;
  }


  fieldsArrayFromObj(obj) {
    const fields: Field[] = [];
    if (typeof obj !== 'object' || !obj) return [];
    Object.keys(obj).forEach(key => fields.push({key, value: obj[key], name: key}));
    return fields;
  }

  getFieldSemanticType(field): FieldSemanticType {
    //('attachmentField' | 'commentArray'| 'attachmentArray' | 'percentage' | 'commentField' | 'stringArray' | 'objectArray')
    if (this.isCommentArray(field)) return 'commentArray';
    if (this.isAttachmentArray(field)) return 'attachmentArray';
    if (this.isAttachmentArray(field)) return 'attachmentArray';
    if (this.isObjectArray(field)) return 'objectArray';
    if (this.isStringArray(field)) return 'stringArray';
    if (this.isAttachmentField(field)) return 'attachmentField';
    if (this.isCommentField(field)) return 'commentField';
    if (this.isPercentageField(field)) return 'percentage';
    return null;
  }


}
